import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { CustomSelect } from '@/components/CustomSelect';
import { Building2, ArrowLeft, Check, Plus } from 'lucide-react';

const inputCls = "w-full bg-white border border-[#E2DDD3] rounded-lg px-4 py-3 text-[14px] text-[#1C1C1C] font-sans placeholder:text-[#D9D3C8] focus:border-[#1F3A34] focus:ring-1 focus:ring-[#1F3A34]/20 focus:outline-none transition-all";
const labelCls = "text-[12px] text-[#5A5A5A] font-sans font-medium mb-1.5 block";

export default function AddAsset() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [portfolios, setPortfolios] = useState([]);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [assetId, setAssetId] = useState(null);
  const [showNewPf, setShowNewPf] = useState(false);
  const [newPfName, setNewPfName] = useState('');
  const [newPfType, setNewPfType] = useState('bv');
  const [savingPf, setSavingPf] = useState(false);

  // Asset fields
  const [pfId, setPfId] = useState(searchParams.get('portfolio_id') || '');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [propType, setPropType] = useState('residential');
  const [marketValue, setMarketValue] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [annualRent, setAnnualRent] = useState('');
  const [vacancy, setVacancy] = useState('2');
  const [opCosts, setOpCosts] = useState('');

  // Loan fields
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [termYears, setTermYears] = useState('30');
  const [lender, setLender] = useState('');
  const [startDate, setStartDate] = useState('');
  const [resetDate, setResetDate] = useState('');
  const [amortization, setAmortization] = useState('annuity');

  const loadPortfolios = () => {
    api.get('/portfolios').then(({ data }) => {
      setPortfolios(data);
      if (!pfId && data.length > 0) setPfId(data[0].portfolio_id);
    }).catch(console.error);
  };

  useEffect(() => { loadPortfolios(); }, []);

  const handleSelectPortfolio = (val) => {
    if (val === '__new__') { setShowNewPf(true); }
    else { setPfId(val); setShowNewPf(false); }
  };

  const createPortfolio = async () => {
    if (!newPfName) return;
    setSavingPf(true);
    try {
      const { data } = await api.post('/portfolios', { name: newPfName, entity_type: newPfType });
      setPortfolios(prev => [...prev, data]);
      setPfId(data.portfolio_id);
      setShowNewPf(false);
      setNewPfName('');
    } catch (e) { console.error(e); }
    finally { setSavingPf(false); }
  };

  const saveAsset = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/assets', {
        portfolio_id: pfId, name, address, city, property_type: propType,
        market_value: parseFloat(marketValue) || 0,
        purchase_price: parseFloat(purchasePrice) || 0,
        annual_rent: parseFloat(annualRent) || 0,
        vacancy_rate: parseFloat(vacancy) || 0,
        operating_costs: parseFloat(opCosts) || 0,
      });
      setAssetId(data.asset_id);
      setStep(1);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveLoan = async () => {
    setLoading(true);
    try {
      await api.post('/loans', {
        asset_id: assetId,
        amount: parseFloat(loanAmount) || 0,
        interest_rate: parseFloat(interestRate) || 0,
        term_years: parseInt(termYears) || 30,
        lender, start_date: startDate, rate_reset_date: resetDate, amortization,
      });
      setStep(2);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const skipLoan = () => setStep(2);

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-fade-in">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="text-[#8C877F] hover:text-[#1F3A34]"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium">Add Property</p>
          <h1 className="font-serif text-[28px] text-[#1C1C1C] font-medium">
            {step === 0 ? 'Property Details' : step === 1 ? 'Financing' : 'Complete'}
          </h1>
        </div>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-2">
        {['Property', 'Financing', 'Done'].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-[12px] font-sans font-semibold ${
              i <= step ? 'bg-[#1F3A34] text-white' : 'bg-[#ECE7DE] text-[#8C877F]'
            }`}>{i < step ? <Check className="w-4 h-4" /> : i + 1}</div>
            <span className="text-[12px] text-[#8C877F] font-sans">{s}</span>
            {i < 2 && <div className={`w-8 h-px ${i < step ? 'bg-[#1F3A34]' : 'bg-[#E2DDD3]'}`} />}
          </div>
        ))}
      </div>

      {/* Step 0: Asset */}
      {step === 0 && (
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div>
            <label className={labelCls}>Portfolio / BV</label>
            <CustomSelect value={pfId} onChange={handleSelectPortfolio} testId="select-portfolio"
              options={[...portfolios.map(p => ({ value: p.portfolio_id, label: p.name, sub: p.entity_type || '' })), { value: '__new__', label: '+ New Portfolio / BV' }]} />
            {showNewPf && (
              <div className="mt-3 p-4 bg-[#F4F1EB] rounded-xl space-y-3 animate-fade-in">
                <p className="text-[12px] text-[#1C1C1C] font-sans font-semibold">Create New Portfolio / BV</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[10px] text-[#5A5A5A] font-sans font-medium mb-1 block">Name *</label><input className={inputCls} value={newPfName} onChange={e => setNewPfName(e.target.value)} placeholder="e.g. Vastgoed BV" /></div>
                  <div><label className="text-[10px] text-[#5A5A5A] font-sans font-medium mb-1 block">Entity Type</label>
                    <CustomSelect value={newPfType} onChange={setNewPfType} options={[
                      { value: 'bv', label: 'BV', sub: 'Besloten vennootschap' },
                      { value: 'nv', label: 'NV', sub: 'Naamloze vennootschap' },
                      { value: 'prive', label: 'Privé', sub: 'Personal holdings' },
                      { value: 'fonds', label: 'Fonds', sub: 'Investment fund' },
                      { value: 'stichting', label: 'Stichting', sub: 'Foundation' },
                    ]} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={createPortfolio} disabled={!newPfName || savingPf}
                    className="bg-[#1F3A34] text-white hover:bg-[#285047] rounded-lg text-[12px] px-4 py-2 font-sans font-medium disabled:opacity-40">
                    {savingPf ? 'Creating...' : 'Create'}
                  </button>
                  <button onClick={() => { setShowNewPf(false); if (portfolios.length > 0) setPfId(portfolios[0].portfolio_id); }}
                    className="border border-[#E2DDD3] text-[#5A5A5A] rounded-lg text-[12px] px-4 py-2 font-sans font-medium">Cancel</button>
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Property Name *</label><input className={inputCls} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Herengracht 100" /></div>
            <div><label className={labelCls}>Type</label>
              <CustomSelect value={propType} onChange={setPropType} options={[
                {value:'residential',label:'Residential'},{value:'commercial',label:'Commercial'},{value:'mixed',label:'Mixed-Use'},{value:'retail',label:'Retail'}
              ]} testId="select-type" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Address</label><input className={inputCls} value={address} onChange={e => setAddress(e.target.value)} placeholder="Street + number" /></div>
            <div><label className={labelCls}>City</label><input className={inputCls} value={city} onChange={e => setCity(e.target.value)} placeholder="City" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Market Value (EUR) *</label><input type="number" className={inputCls} value={marketValue} onChange={e => setMarketValue(e.target.value)} placeholder="850000" /></div>
            <div><label className={labelCls}>Purchase Price (EUR)</label><input type="number" className={inputCls} value={purchasePrice} onChange={e => setPurchasePrice(e.target.value)} placeholder="720000" /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className={labelCls}>Annual Rent (EUR)</label><input type="number" className={inputCls} value={annualRent} onChange={e => setAnnualRent(e.target.value)} placeholder="42000" /></div>
            <div><label className={labelCls}>Vacancy (%)</label><input type="number" className={inputCls} value={vacancy} onChange={e => setVacancy(e.target.value)} placeholder="2" /></div>
            <div><label className={labelCls}>Operating Costs (EUR/yr)</label><input type="number" className={inputCls} value={opCosts} onChange={e => setOpCosts(e.target.value)} placeholder="4200" /></div>
          </div>
          <button onClick={saveAsset} disabled={!name || !marketValue || !pfId || loading}
            className="w-full bg-[#1F3A34] text-white hover:bg-[#285047] rounded-lg text-[14px] py-3.5 font-sans font-medium transition-colors disabled:opacity-40">
            {loading ? 'Saving...' : 'Continue to Financing'}
          </button>
        </div>
      )}

      {/* Step 1: Loan */}
      {step === 1 && (
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Loan Amount (EUR) *</label><input type="number" className={inputCls} value={loanAmount} onChange={e => setLoanAmount(e.target.value)} placeholder="510000" /></div>
            <div><label className={labelCls}>Interest Rate (%) *</label><input type="number" step="0.1" className={inputCls} value={interestRate} onChange={e => setInterestRate(e.target.value)} placeholder="3.6" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Term (years)</label><input type="number" className={inputCls} value={termYears} onChange={e => setTermYears(e.target.value)} placeholder="30" /></div>
            <div><label className={labelCls}>Lender</label><input className={inputCls} value={lender} onChange={e => setLender(e.target.value)} placeholder="ING Bank" /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className={labelCls}>Start Date</label><input type="date" className={inputCls} value={startDate} onChange={e => setStartDate(e.target.value)} /></div>
            <div><label className={labelCls}>Rate Reset Date</label><input type="date" className={inputCls} value={resetDate} onChange={e => setResetDate(e.target.value)} /></div>
            <div><label className={labelCls}>Amortization</label>
              <CustomSelect value={amortization} onChange={setAmortization} options={[
                {value:'annuity',label:'Annuity'},{value:'linear',label:'Linear'},{value:'interest_only',label:'Interest Only'}
              ]} testId="select-amort" />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={saveLoan} disabled={!loanAmount || !interestRate || loading}
              className="flex-1 bg-[#1F3A34] text-white hover:bg-[#285047] rounded-lg text-[14px] py-3.5 font-sans font-medium transition-colors disabled:opacity-40">
              {loading ? 'Saving...' : 'Save Loan & Finish'}
            </button>
            <button onClick={skipLoan} className="px-6 border border-[#E2DDD3] text-[#5A5A5A] hover:border-[#1F3A34]/30 rounded-lg text-[14px] py-3.5 font-sans font-medium transition-colors">
              Skip (No Loan)
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Done */}
      {step === 2 && (
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-8 text-center space-y-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div className="w-14 h-14 rounded-xl bg-[#E6EFEA] mx-auto flex items-center justify-center">
            <Check className="w-7 h-7 text-[#1F3A34]" />
          </div>
          <div>
            <h2 className="font-serif text-[24px] text-[#1C1C1C] font-medium">{name} Added</h2>
            <p className="text-[14px] text-[#8C877F] mt-1">Property and financing saved. You can now add rent contracts.</p>
          </div>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate(`/contracts?add=true&asset_id=${assetId}`)}
              className="bg-[#1F3A34] text-white hover:bg-[#285047] rounded-lg text-[14px] px-6 py-3 font-sans font-medium transition-colors">
              Add Rent Contract
            </button>
            <button onClick={() => navigate('/dashboard')}
              className="border border-[#E2DDD3] text-[#5A5A5A] hover:border-[#1F3A34]/30 rounded-lg text-[14px] px-6 py-3 font-sans font-medium transition-colors">
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
